import React from "react";
import { compose, withHandlers, withProps } from "recompose";
import { Row, Col, PopoverHeader, PopoverBody, Button } from "reactstrap";
import { Control, actions } from "react-redux-form";
import { withTranslation, Trans } from "react-i18next";
import shortid from "shortid";
import deepPick from "deepPick";
import { NotificationManager } from "react-notifications";
import { get } from "lodash";
import {
  Page,
  onMount,
  withReload,
  withLocalForm,
  FormGroup,
  DatePicker,
  SubmitButton,
  required,
  withPopover,
} from "components/common";
import {
  withXeroErrors,
  withContactSearch,
  LineItems,
  SalesEditActions,
  Attachments,
  withOptions,
  withDocumentRenderer,
} from "components";
import { PurchaseOrderDocument } from "components/documents";
import { formatDate } from "../../../../services/util";
import moment from "moment";
import BackButton from "components/refactoring/BackButton";
import {filterAndValidateLineItems, getBlankLineItems} from "../../../../components/LineItems";

export const Edit = ({
  state,
  t,
  LocalForm,
  ContactSearch,
  XeroErrors,
  history,
  dispatch,
  xero,
  handleSave,
  handleAttachmentsChanged,
  PurchaseOrderOptions,
  Popover,
  togglePopover,
  DocumentRenderer,
  DownloadDocument,
  EmailDocument,
}) => {

  const viewLink = () => {
    const { PurchaseOrderID } = state;

    history.push(`/purchases/purchase_orders/view/${PurchaseOrderID}`);
  };

  return (
  <Page
    translate={false}
    title={t(
      state.PurchaseOrderID
        ? "Edit Purchase Order {{ PurchaseOrderNumber }}"
        : "New Purchase Order",
      state
    )}
  > 
    <BackButton/>
      <br/>
    <XeroErrors />
    <DocumentRenderer component={PurchaseOrderDocument} />
    <LocalForm
      validators={{
        Contact: { required },
        Date: { required },
      }}
    >
      <div className="d-flex">
        {state.SentToContact && (
          <div className="text-success small">
            <span className="fa fa-check mr-1" /> <Trans>Sent to contact</Trans>
          </div>
        )}
        <div className="mx-auto" />
        {state.PurchaseOrderID && (
          <EmailDocument className="mr-2" type="PURCHASE_ORDER" item={state} />
        )}
        {state.PurchaseOrderID && <DownloadDocument />}
        {state.Status === "AUTHORISED" && (
          <div>
            <Button
              id="mark-as-billed"
              onClick={togglePopover}
              className="mr-2"
              size="sm"
              color="success"
            >
              <Trans>Mark as Billed</Trans>
            </Button>
            <Popover placement="bottom" target="#mark-as-billed">
              <PopoverHeader>
                <Trans>
                  Mark {{ PurchaseOrderNumber: state.PurchaseOrderNumber }} as
                  billed
                </Trans>
              </PopoverHeader>
              <PopoverBody>
                <FormGroup check label="Copy purchase order to a draft bill">
                  <Control.checkbox model=".copyToBill" />
                </FormGroup>
                <div className="text-right mt-3">
                  <SubmitButton
                    size="sm"
                    onClick={() => handleSave({ billed: true })}
                  >
                    Confirm
                  </SubmitButton>
                </div>
              </PopoverBody>
            </Popover>
          </div>
        )}
        {state.PurchaseOrderID && state.Attachments ? (
          <Attachments
            xero={xero}
            hideIncludeOnline
            onAttachmentsChanged={handleAttachmentsChanged}
            baseUrl={`purchaseorders/${state.PurchaseOrderID}`}
            attachments={state.Attachments}
          />
        ) : null}
        {state.PurchaseOrderID && <PurchaseOrderOptions />}
        <div className="">
        {state.PurchaseOrderID && (state.Status === "DRAFT" || state.Status === "SUBMITTED") && 
          <button
             type="button"
             className="btn-sm btn btn-primary"
             onClick={viewLink}>
             View Purchase Order
          </button>
        }
        </div>
      </div>
      <Row className="mt-3">
        <Col xs={false} md={6}>
          <FormGroup label="To" model=".Contact" addControlClass={false}>
            <ContactSearch model=".Contact" style={{backgroundColor:'#F4F7FE'}}/>
            {state.Contact && state.Contact.AccountNumber ? (
              <p className="form-text small text-muted">
                <Trans>
                  Account No. {{ AccountNumber: state.Contact.AccountNumber }}
                </Trans>
              </p>
            ) : null}
          </FormGroup>
        </Col>
        <Col xs={false} md={6}>
          <FormGroup label="Status">
            <Control.text model=".Status" readOnly />
          </FormGroup>
        </Col>
        <Col xs={false} md={6}>
          <FormGroup label="Date" model=".Date" className="pt-2 pb-2">
            <DatePicker model=".Date" className="form-control" />
          </FormGroup>
        </Col>
        <Col xs={false} md={6}>
          <FormGroup label="Delivery Date" model=".DeliveryDate" className="pt-2 pb-2">
            <DatePicker model=".DeliveryDate" className="form-control" />
          </FormGroup>
        </Col>
        <Col xs={false} md={6}>
          <FormGroup label="Order number" model=".PurchaseOrderNumber" className="pt-2 pb-2">
            <Control.text
              placeholder="PO-AUTO"
              readOnly={!state.PurchaseOrderNumber}
              updateOn="blur"
              model=".PurchaseOrderNumber"
            />
          </FormGroup>
        </Col>
        <Col xs={false} md={6}>
          <FormGroup label="Reference" model=".Reference" className="pt-2 pb-2">
            <Control.text updateOn="blur" model=".Reference" />
          </FormGroup>
        </Col>
      </Row>
      <hr />
      <LineItems includeDiscount item={state} dispatch={dispatch} xero={xero} />
      <hr />
      <FormGroup label="Delivery Address" model=".DeliveryAddress" className="pt-2 pb-2">
        <Control.textarea model=".DeliveryAddress" rows="4" style={{backgroundColor:'#F4F7FE'}}/>
      </FormGroup>
      <Row>
        <Col xs={false} md={6}>
          <FormGroup label="Attention" model=".AttentionTo" className="pt-2 pb-2">
            <Control.text model=".AttentionTo" />
          </FormGroup>
        </Col>
        <Col xs={false} md={6}>
          <FormGroup label="Telephone" model=".Telephone" className="pt-2 pb-2">
            <Control.text model=".Telephone" />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup label="Delivery Instructions" model=".DeliveryInstructions" className="pt-2 pb-2">
        <Control.textarea model=".DeliveryInstructions" rows="4" style={{backgroundColor:'#F4F7FE'}}/>
      </FormGroup>
      <SalesEditActions onSave={handleSave} item={state} />
    </LocalForm>
  </Page>
)};

const initialState = {
  Type: "PURCHASEORDER",
  LineItems: getBlankLineItems(),
  Date: formatDate(moment()),
  Status: "DRAFT",
};

export const enhance = compose(
  withTranslation(),
  withReload(),
  withXeroErrors(),
  onMount(
    ({
        xero,
        location,
        match: {
          params: { id },
        },
      }) =>
      () =>
        Promise.props({
          state:
            id === "new"
              ? get(location, "state.copy") || initialState
              : xero
                  .get(`purchaseorders/${id}`)
                  .get("data")
                  .then(({ LineItems, ...data }) => ({
                    ...data,
                    LineItems: LineItems.map((i) => ({
                      ...i,
                      _id: shortid(),
                    })),
                  })),
        })
  ),
  withLocalForm({
    initialState: (props) => props.state,
  }),
  withContactSearch(),
  withPopover(),
  withProps(({ state: item }) => ({ item })),
  withOptions(),
  withHandlers({
    handleAttachmentsChanged:
      ({ xero, match, dispatch }) =>
      () => {
        xero
          .get(`purchaseorders/${match.params.id}/attachments`)
          .get("data")
          .tap((d) => dispatch(actions.change("local.Attachments", d)));
      },
    handleSave:
      ({ state, xero, reload, history, match, setXeroErrors, t, store, dispatch }) =>
      ({ draft, approve, submit, addAnother, billed } = {}) => {
        const { valid } = store.getState().forms.$form;
        if (!valid) return;

        let { result: LineItems, error } = filterAndValidateLineItems(
          dispatch
        )(state.LineItems);

        if (error) {
          return;
        }

        const idKey = "PurchaseOrderID";
        const numberKey = "PurchaseOrderNumber";
        const url = "purchaseorders";
        const entity = "Purchase Order";
        const allowedProps = [
          "Contact",
          "LineItems",
          "Date",
          "DeliveryDate",
          "LineAmountTypes",
          "PurchaseOrderNumber",
          "PurchaseOrderID",
          "Reference",
          "CurrencyCode",
          "Status",
          "DeliveryAddress",
          "AttentionTo",
          "Telephone",
          "DeliveryInstructions",
          "Contact.ContactID",
        ];

        let Status = state.Status;
        if (draft) Status = "DRAFT";
        if (approve) Status = "AUTHORISED";
        if (submit) Status = "SUBMITTED";
        if (billed) Status = "BILLED";
        setXeroErrors(null);

        const id = state[idKey];

        const allowedItem = deepPick(state, allowedProps);

        LineItems = LineItems.map(i => ({
          ...i,
          DiscountAmount: undefined,
          TaxAmount: undefined,
          LineAmount: undefined
        }))

        const p = id
          ? xero.put(`${url}/${id}`, {
              ...allowedItem,
              Status,
              LineItems,
            })
          : xero.post(url, {
              ...allowedItem,
              [numberKey]: undefined,
              Status,
              LineItems
            });

        p.then(({ data }) => {
          NotificationManager.success(t(`${entity} saved successfully`));

          if (state.copyToBill) {
            history.push(`/purchases/bills/edit/new`, {
              copy: {
                  ...state,
                  Status: "DRAFT",
                  InvoiceNumber: state.Reference,
                  Type: "ACCPAY",
              },
          });
          } else if (addAnother) {
            history.push(match.path.replace(":id", "new"));
            reload();
          } else {
            history.replace(`/purchases/purchase_orders/view/${data[idKey]}`);
            reload();
          }
        }, setXeroErrors);
      },
  }),
  withDocumentRenderer({
    onDownload:
      ({ item }) =>
      (getDocument) =>
        getDocument({ item }, `purchaseorder_${item.PurchaseOrderNumber}.pdf`),
  })
);

export default enhance(Edit);
